import React, { useState, useCallback, useEffect } from "react";
import { Button, Modal, message as antMessage } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useIntl } from "react-intl";

interface DraggableCloseButtonProps {
  onClose: () => void;
  disabled?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
}

const DraggableCloseButton: React.FC<DraggableCloseButtonProps> = ({
  onClose,
  disabled = false,
  containerRef,
}) => {
  const intl = useIntl();
  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 }); // 初始位置，稍后计算
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // 处理关闭聊天
  const handleCloseChat = useCallback(async () => {
    if (isDragging) return;

    Modal.confirm({
      title: intl.formatMessage({ id: "close.chat.confirm.title" }),
      content: intl.formatMessage({ id: "close.chat.confirm.content" }),
      okText: intl.formatMessage({ id: "close.chat.confirm.ok" }),
      cancelText: intl.formatMessage({ id: "close.chat.confirm.cancel" }),
      onOk: async () => {
        try {
          await onClose();
          antMessage.success(intl.formatMessage({ id: "close.chat.success" }));
        } catch (error) {
          console.error("❌ 关闭聊天失败:", error);
          antMessage.error(intl.formatMessage({ id: "close.chat.error" }));
        }
      },
    });
  }, [isDragging, onClose, intl]);

  // 获取事件坐标（兼容鼠标和触摸）
  const getEventCoordinates = (e: MouseEvent | TouchEvent) => {
    if ("touches" in e) {
      return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    return { x: e.clientX, y: e.clientY };
  };

  // 拖拽开始（鼠标）
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
    });
  }, []);

  // 拖拽开始（触摸）
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    });
  }, []);

  // 拖拽移动
  const handleMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging) return;

      const { x: currentX, y: currentY } = getEventCoordinates(e);

      // 修正拖拽方向逻辑 - 鼠标向哪移动，按钮就向哪移动
      const deltaX = currentX - dragStart.x;
      const deltaY = currentY - dragStart.y;

      // 使用视窗尺寸进行边界检测
      const buttonSize = 44;

      // 计算新位置，确保按钮不会超出视窗边界
      const newX = Math.max(
        0,
        Math.min(window.innerWidth - buttonSize, dragPosition.x + deltaX)
      );
      const newY = Math.max(
        0,
        Math.min(window.innerHeight - buttonSize, dragPosition.y + deltaY)
      );

      setDragPosition({ x: newX, y: newY });

      setDragStart({
        x: currentX,
        y: currentY,
      });
    },
    [isDragging, dragStart, dragPosition]
  );

  // 拖拽结束
  const handleEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // 添加全局事件监听（支持鼠标和触摸）
  useEffect(() => {
    if (isDragging) {
      // 鼠标事件
      document.addEventListener("mousemove", handleMove);
      document.addEventListener("mouseup", handleEnd);

      // 触摸事件
      document.addEventListener("touchmove", handleMove, { passive: false });
      document.addEventListener("touchend", handleEnd);

      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleMove);
        document.removeEventListener("mouseup", handleEnd);
        document.removeEventListener("touchmove", handleMove);
        document.removeEventListener("touchend", handleEnd);
        document.body.style.userSelect = "";
      };
    }
  }, [isDragging, handleMove, handleEnd]);

  // 初始化按钮位置（固定在提交按钮上方）
  useEffect(() => {
    const initializePosition = () => {
      if (dragPosition.x === 0 && dragPosition.y === 0) {
        const buttonSize = 44;
        const padding = 20;

        // 使用视窗尺寸，固定在提交按钮上方
        // 位置：右下角，在提交按钮正上方
        const initialX = window.innerWidth - buttonSize - padding;
        const initialY = window.innerHeight - buttonSize - padding - 80; // 在提交按钮上方20px

        setDragPosition({
          x: Math.max(padding, initialX),
          y: Math.max(padding, initialY),
        });
      }
    };

    // 延迟初始化，确保容器已渲染
    const timer = setTimeout(initializePosition, 100);
    return () => clearTimeout(timer);
  }, [dragPosition.x, dragPosition.y]);

  // 监听窗口大小变化，重新调整位置
  useEffect(() => {
    const handleResize = () => {
      const buttonSize = 44;
      const padding = 20;

      // 重新计算位置，确保按钮在视窗内，保持在提交按钮上方
      const newX = Math.max(
        padding,
        Math.min(window.innerWidth - buttonSize - padding, dragPosition.x)
      );
      const newY = Math.max(
        padding,
        Math.min(window.innerHeight - buttonSize - padding - 80, dragPosition.y)
      );

      setDragPosition({ x: newX, y: newY });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dragPosition.x, dragPosition.y]);

  return (
    <Button
      type="default"
      shape="circle"
      icon={<CloseOutlined />}
      onClick={handleCloseChat}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      disabled={disabled}
      title={
        isDragging
          ? intl.formatMessage({ id: "close.button.title.dragging" })
          : intl.formatMessage({ id: "close.button.title.normal" })
      }
      style={{
        position: "fixed",
        top: `${dragPosition.y}px`,
        left: `${dragPosition.x}px`,
        width: "44px",
        height: "44px",
        borderRadius: "50%",
        border: isDragging ? "2px solid #1890ff" : "1px solid #40a9ff",
        color: isDragging ? "#0050b3" : "#1890ff",
        background: isDragging
          ? "rgba(24, 144, 255, 0.1)"
          : "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(8px)",
        fontSize: "14px",
        fontWeight: 500,
        boxShadow: isDragging
          ? "0 8px 24px rgba(24, 144, 255, 0.25), 0 4px 12px rgba(24, 144, 255, 0.15)"
          : "0 4px 12px rgba(24, 144, 255, 0.12), 0 2px 6px rgba(24, 144, 255, 0.08)",
        transition: isDragging
          ? "none"
          : "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: isDragging ? "grabbing" : disabled ? "not-allowed" : "grab",
        opacity: disabled ? 0.5 : 1,
        transform: isDragging ? "scale(1.05)" : "scale(1)",
        userSelect: "none",
      }}
      onMouseEnter={(e) => {
        if (disabled || isDragging) return;
        const target = e.target as HTMLElement;
        target.style.background = "rgba(24, 144, 255, 0.08)";
        target.style.color = "#0050b3";
        target.style.transform = "scale(1.08)";
        target.style.borderColor = "#1890ff";
        target.style.boxShadow =
          "0 6px 16px rgba(24, 144, 255, 0.2), 0 3px 8px rgba(24, 144, 255, 0.12)";
      }}
      onMouseLeave={(e) => {
        if (disabled || isDragging) return;
        const target = e.target as HTMLElement;
        target.style.background = "rgba(255, 255, 255, 0.95)";
        target.style.color = "#1890ff";
        target.style.transform = "scale(1)";
        target.style.borderColor = "#40a9ff";
        target.style.boxShadow =
          "0 4px 12px rgba(24, 144, 255, 0.12), 0 2px 6px rgba(24, 144, 255, 0.08)";
      }}
    />
  );
};

export default DraggableCloseButton;
